import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react';
import type { RealtimeParticipantIdentity } from 'services';
import type { EmbeddedUser } from 'modules/core/types';
import useSWR from 'swr';
import invariant from 'tiny-invariant';

// Distinct, deterministic per-user marker colors.
const PALETTE = ['#e6194b', '#3cb44b', '#4363d8', '#f58231', '#911eb4', '#008080', '#f032e6', '#9a6324'];

export function presenceColor(userId: number): string {
  return PALETTE[userId % PALETTE.length];
}

type PresenceUsersContextType = {
  users: EmbeddedUser[];
  color: (userId: number) => string;
  user: (userId: number) => EmbeddedUser | undefined;
};

const PresenceUsersContext = createContext<PresenceUsersContextType | null>(null);

/**
 * Resolves the channel's present `user_id`s to real users with a single batched
 * fetch, shared by every consumer below it (block header avatars, plan markers).
 * Presence transport remains owned by the feature provider; this component only resolves the
 * participant ids it receives.
 */
export function PresenceUsersProvider({
  participants,
  children,
}: {
  participants: RealtimeParticipantIdentity[];
  children: ReactNode;
}): ReactElement {
  const userIds = useMemo(
    () => [...new Set(participants.map((participant) => participant.userId))].sort((a, b) => a - b),
    [participants],
  );

  const { data } = useSWR<EmbeddedUser[]>(
    userIds.length ? { method: 'GET', url: 'auth/users/', queryParams: { id__in: userIds.join(',') } } : null,
  );

  const value = useMemo<PresenceUsersContextType>(() => {
    const users = data ?? [];
    const byId = new Map(users.map((user) => [user.id, user]));
    return {
      users,
      color: presenceColor,
      user: (userId) => byId.get(userId),
    };
  }, [data]);

  return <PresenceUsersContext.Provider value={value}>{children}</PresenceUsersContext.Provider>;
}

export function usePresenceUsers(): PresenceUsersContextType {
  const ctx = useContext(PresenceUsersContext);
  invariant(ctx, 'usePresenceUsers must be used within a PresenceUsersProvider');
  return ctx;
}
